import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWAInstall } from '../contexts/PWAInstallContext';
import { Download, Smartphone, ChevronRight, CheckCircle2 } from 'lucide-react';

/**
 * Persistent "Install App" row.
 *
 * Designed to slot into any settings/sidebar list using the same visual
 * grammar as the existing rows (Profile.jsx Quick Actions, BusinessDashboard
 * sidebar, FrontPage). The single tap invokes `openInstallFlow(surface)`
 * which either:
 *   1. Calls the native Chrome install prompt if available, OR
 *   2. Opens the platform-appropriate help modal (iOS / Android / Desktop /
 *      in-app-browser) hoisted at the context level.
 *
 * Renders nothing if the app is already running in standalone mode (i.e.
 * already installed) — there's nothing for the user to do.
 *
 * `surface` is forwarded into the PostHog events for funnel analysis.
 */
const InstallAppMenuItem = ({
  surface = 'menu',
  variant = 'row', // 'row' | 'card'
  testId,
}) => {
  const { t } = useTranslation();
  const {
    installed, canNativeInstall, platform,
    openInstallFlow, markInstallSurfaceVisible,
  } = usePWAInstall();

  useEffect(() => {
    if (!installed) markInstallSurfaceVisible(surface);
  }, [installed, surface, markInstallSurfaceVisible]);

  if (installed) return null;

  const handleClick = () => openInstallFlow(surface);

  // Sub-label adapts to the device so the user knows what tapping does.
  const subLabel = canNativeInstall
    ? t('installApp.subLabelNative', 'One tap to install on your Home Screen')
    : platform.isInAppBrowser
      ? t('installApp.subLabelInApp', 'You need to open Find-2-Day in Chrome or Safari first — tap for steps')
      : platform.isIOS
        ? t('installApp.subLabelIOS', 'iPhone: Share → Add to Home Screen — tap for steps')
        : platform.isAndroid
          ? t('installApp.subLabelAndroid', 'Android: ⋮ menu → Install app — tap for steps')
          : t('installApp.subLabelDesktop', 'Install icon in the address bar — tap for steps');

  if (variant === 'card') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 p-4 hover:border-purple-300 hover:shadow-md transition-all"
        data-testid={testId || 'install-app-card'}
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center">
            {canNativeInstall ? <Download className="h-5 w-5" /> : <Smartphone className="h-5 w-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-purple-900 text-sm">
              {t('installApp.title', 'Install Find-2-Day as an app')}
            </p>
            <p className="text-xs text-purple-800/80 mt-0.5 leading-snug">{subLabel}</p>
          </div>
          <ChevronRight className="h-5 w-5 text-purple-400 shrink-0 mt-1" />
        </div>
      </button>
    );
  }

  // Default 'row' — matches Profile.jsx Quick Actions style.
  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 rounded-lg transition-colors text-left"
      data-testid={testId || 'install-app-row'}
    >
      <div className="p-2 bg-purple-100 rounded-lg">
        {canNativeInstall
          ? <Download className="h-5 w-5 text-purple-600" />
          : <Smartphone className="h-5 w-5 text-purple-600" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-gray-900 font-medium">
          {t('installApp.title', 'Install Find-2-Day as an app')}
        </p>
        <p className="text-xs text-gray-500 mt-0.5 truncate">{subLabel}</p>
      </div>
      {canNativeInstall ? (
        <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
      ) : (
        <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
      )}
    </button>
  );
};

export default InstallAppMenuItem;
